package com.shadowings.gero.data.lessons.chapter16

import androidx.compose.ui.unit.dp
import com.shadowings.gero.data.lessons.buildVocabQuizContent
import com.shadowings.gero.data.lessons.chapter12.buildMultiPress
import com.shadowings.gero.data.lessons.inlineMeaning
import com.shadowings.gero.domain.model.Characters
import com.shadowings.gero.domain.model.ExplanationContent
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.GameConstants
import com.shadowings.gero.domain.model.IExplanation
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.model.Word
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.exp_content_scorta_di_aggettivi_1
import gero.composeapp.generated.resources.exp_content_scorta_di_aggettivi_2
import gero.composeapp.generated.resources.exp_content_scorta_di_aggettivi_3
import gero.composeapp.generated.resources.exp_content_scorta_di_aggettivi_4
import gero.composeapp.generated.resources.exp_content_scorta_di_aggettivi_5
import gero.composeapp.generated.resources.exp_content_scorta_di_aggettivi_6
import gero.composeapp.generated.resources.exp_content_scorta_di_aggettivi_7
import gero.composeapp.generated.resources.exp_content_word_reading_and_meaning
import gero.composeapp.generated.resources.exp_title_scorta_di_aggettivi
import gero.composeapp.generated.resources.exp_title_word_name
import kotlin.random.Random

// Only the four new adjectives get a slide; the five from Chapter 15 (NOMI_E_AGGETTIVI_1)
// are already explained there and only come back in the quiz, as distractors and review.
val scortaDiAggettiviOldAdjectiveIds = listOf("BUONO", "GRANDE", "PICCOLO", "ECCELLENTE", "TUTTO")
val scortaDiAggettiviNewAdjectiveIds = listOf("CATTIVO", "FORTE", "PURO", "VERO")

// Already-taught nouns, used only as distractors.
private val scortaDiAggettiviDistractorIds = listOf("UOMO", "DONNA", "CASA", "DIO", "SCRIBA", "SEGUACE")

fun getScortaDiAggettiviContent(words: List<Word>, languageCode: String): List<IExplanation> {
    val intro = listOf(
        ExplanationContent(
            title = Res.string.exp_title_scorta_di_aggettivi,
            content = Res.string.exp_content_scorta_di_aggettivi_1,
            face = Faces.Happy,
            character = Characters.Alyn,
        ),
        ExplanationContent(
            title = Res.string.exp_title_scorta_di_aggettivi,
            content = Res.string.exp_content_scorta_di_aggettivi_2,
            face = Faces.Doubt,
            character = Characters.Zeta,
        ),
        ExplanationContent(
            title = Res.string.exp_title_scorta_di_aggettivi,
            content = Res.string.exp_content_scorta_di_aggettivi_3,
            face = Faces.Happy,
            character = Characters.Alyn,
        ),
        ExplanationContent(
            title = Res.string.exp_title_scorta_di_aggettivi,
            content = Res.string.exp_content_scorta_di_aggettivi_4,
            face = Faces.Sad,
            character = Characters.Zeta,
        ),
        ExplanationContent(
            title = Res.string.exp_title_scorta_di_aggettivi,
            content = Res.string.exp_content_scorta_di_aggettivi_5,
            face = Faces.Idle,
            character = Characters.Nilo,
        ),
        ExplanationContent(
            title = Res.string.exp_title_scorta_di_aggettivi,
            content = Res.string.exp_content_scorta_di_aggettivi_6,
            face = Faces.Idle,
            character = Characters.Alyn,
        )
    )

    val slides = scortaDiAggettiviNewAdjectiveIds
        .mapNotNull { id -> words.firstOrNull { it.id == id } }
        .map { word ->
            ExplanationContent(
                title = Res.string.exp_title_word_name,
                titleArgs = listOf(word.getMeaning(languageCode)),
                content = Res.string.exp_content_word_reading_and_meaning,
                contentArgs = listOf(
                    word.reading,
                    word.inlineMeaning(languageCode),
                    word.getNote(languageCode)
                ),
                face = Faces.Happy,
                character = Characters.Alyn,
                composable = { MdCWord(word.gardiner, rtl = false, cart = false, size = 96.dp) },
                wordId = word.id
            )
        }

    // After the new words, Zeta puts bin to use on the heat that pushes the group onto the Nile.
    val outro = listOf(
        ExplanationContent(
            title = Res.string.exp_title_scorta_di_aggettivi,
            content = Res.string.exp_content_scorta_di_aggettivi_7,
            face = Faces.Happy,
            character = Characters.Zeta,
        )
    )

    return intro + slides + outro
}

// One question of a random type per new adjective + a multipress on the new adjectives
// + a multipress mixing new and old ones, topped up to QUIZ_QUESTIONS_COUNT with one question
// each on randomly picked Chapter 15 adjectives.
fun getScortaDiAggettiviQuizContent(
    words: List<Word>,
    languageCode: String,
    random: Random = Random
): List<IQuizContent> {
    val explanations = getScortaDiAggettiviContent(words, languageCode)
    val optionIds = (scortaDiAggettiviNewAdjectiveIds + scortaDiAggettiviOldAdjectiveIds + scortaDiAggettiviDistractorIds).toSet()

    val wordQuestions = explanations
        .filterIsInstance<ExplanationContent>()
        .filter { it.wordId != null }
        .mapNotNull { slide ->
            buildVocabQuizContent(
                explanations = listOf(slide),
                words = words,
                languageCode = languageCode,
                additionalOptions = optionIds
            ).randomOrNull(random)
        }

    val newWords = scortaDiAggettiviNewAdjectiveIds.mapNotNull { id -> words.firstOrNull { it.id == id } }
    val oldWords = scortaDiAggettiviOldAdjectiveIds.mapNotNull { id -> words.firstOrNull { it.id == id } }

    val multiPressNew = buildMultiPress(newWords.shuffled(random), languageCode)
    val multiPressMixed = buildMultiPress(
        (newWords.shuffled(random).take(2) + oldWords.shuffled(random).take(2)).shuffled(random),
        languageCode
    )

    val baseQuestions = wordQuestions + multiPressNew + multiPressMixed
    val oldWordReview = oldWords
        .shuffled(random)
        .take((GameConstants.QUIZ_QUESTIONS_COUNT - baseQuestions.size).coerceAtLeast(0))
        .mapNotNull { word ->
            buildVocabQuizContent(
                explanations = listOf(reviewSlide(word)),
                words = words,
                languageCode = languageCode,
                additionalOptions = optionIds
            ).randomOrNull(random)
        }

    return (baseQuestions + oldWordReview).shuffled(random)
}

// buildVocabQuizContent only reads the wordId of its explanations.
private fun reviewSlide(word: Word): IExplanation =
    ExplanationContent(
        title = Res.string.exp_title_word_name,
        content = Res.string.exp_content_word_reading_and_meaning,
        face = Faces.Happy,
        wordId = word.id
    )
